import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import type {
  InputMaybe,
  InsertDocumentVersionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

interface InsertDocumentVersionTRPCSharedInput {
  ParentDocumentId: string;
  Version?: InputMaybe<string>;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type InsertDocumentVersionTRPCTypeInput =
  | { Type: 'file'; FileId: string }
  | { Type: 'html'; Content: string }
  | { Type: 'link'; Link: string };

export type InsertDocumentVersionTRPCInput =
  InsertDocumentVersionTRPCSharedInput & InsertDocumentVersionTRPCTypeInput;

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 * so consumers can be migrated 1:1.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertDocumentVersionMutation => {
  return {
    insertDocumentVersion: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

type InsertDocumentVersionTRPCPayload = InsertDocumentVersionTRPCSharedInput &
  (
    | { Type: DocumentFileType.File; FileId: string }
    | { Type: DocumentFileType.Html; Content: string }
    | { Type: DocumentFileType.Link; Link: string }
  );

/**
 * Translates the loose, string-typed `Type` field used by the form layer into
 * the typed discriminated union the tRPC procedure expects.
 */
const toTRPCPayload = (
  variables: InsertDocumentVersionTRPCInput
): InsertDocumentVersionTRPCPayload => {
  const shared = {
    ParentDocumentId: variables.ParentDocumentId,
    Version: variables.Version,
    Summary: variables.Summary,
    ReasonForReview: variables.ReasonForReview,
    ReviewedBy: variables.ReviewedBy,
    ReviewDate: variables.ReviewDate,
    NextReviewDate: variables.NextReviewDate,
    CustomAttributeData: variables.CustomAttributeData,
  };
  switch (variables.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: variables.FileId,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        Content: variables.Content,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        Link: variables.Link,
      };
  }
};

export const useInsertDocumentVersionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.documentFile.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.latestDocumentFile.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.documentFileById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `document` (a new version
        // changes the parent document's current-version surfaces).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.pathKey(),
        });
      },
    }),
  });

  return {
    insertDocumentVersion: async (
      variables: InsertDocumentVersionTRPCInput
    ): Promise<InsertDocumentVersionMutation> => {
      const result = await mutation.mutateAsync(toTRPCPayload(variables));

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
